<script setup lang="ts">
/*
 * User trigger in the top-right — markup mirrors the V6 proto's user
 * pill (lines ~9285–9291 of `redesign/optimal-hr-prototype-v6.html`):
 *
 *   <div class="...rounded-xl hover:bg-slate-50">
 *     <div class="h-9 w-9 rounded-full ... gradient">SE</div>
 *     <div>
 *       <div>Sopheap Eng</div>
 *       <div class="text-[11px] text-slate-500">HR Director</div>
 *     </div>
 *   </div>
 *
 * The proto's pill is a flat clickable div; in v2 it opens a Headless UI
 * Menu so users can switch language, view profile, change password, or
 * sign out. The second line shows the user's email (the proto's "role"
 * is demo copy — `claims['custom:role']` is currently a coarse
 * `TenantAdmin` / etc. label, not a job title).
 */
import { computed } from 'vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { LogOut, KeyRound, Languages, User } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useLocale } from '@/composables/useLocale';

const props = defineProps<{
  user: { name: string; email: string };
}>();

defineEmits<{
  signOut: [];
  changePassword: [];
  viewProfile: [];
}>();

const { t } = useI18n();
const { toggle, locale } = useLocale();

function onSwitchLanguage() {
  toggle();
}

// Two-letter initials from "First Last"; fallback to first two
// characters when the name has no space (e.g. "demo").
const initials = computed(() => {
  const parts = props.user.name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return props.user.name.slice(0, 2).toUpperCase();
});

const menuItemClass =
  'group flex w-full items-center gap-2 rounded-md px-3 py-2 text-md hover:bg-slate-50 ui-active:bg-slate-50';
</script>

<template>
  <Menu as="div" class="relative">
    <MenuButton
      class="flex items-center gap-2 rounded-xl py-1 pl-1 pr-2 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      :aria-label="`Open user menu for ${user.name}`"
      data-testid="user-menu-trigger"
    >
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
        style="background: linear-gradient(135deg, #5b4fe8, #9b6be8)"
        aria-hidden="true"
        data-testid="user-menu-avatar"
      >
        {{ initials }}
      </span>
      <!-- Avatar-only below sm so the top bar fits a 480px viewport (Q13 / D2). -->
      <span class="hidden text-left leading-tight sm:block" data-testid="user-menu-identity">
        <span class="block text-sm font-medium text-app-fg" data-testid="user-menu-name">
          {{ user.name }}
        </span>
        <span class="block text-[11px] text-slate-500" data-testid="user-menu-email">
          {{ user.email }}
        </span>
      </span>
    </MenuButton>
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl border border-app-border bg-app-surface p-1 shadow-popup focus:outline-none"
      >
        <div class="px-3 py-2">
          <p class="truncate text-md font-semibold text-app-fg">{{ user.name }}</p>
          <p class="truncate text-xs text-app-muted">{{ user.email }}</p>
        </div>
        <div class="my-1 h-px bg-app-border-soft" />
        <MenuItem>
          <button type="button" :class="menuItemClass" @click="$emit('viewProfile')">
            <User class="h-4 w-4 text-app-muted" aria-hidden="true" />
            {{ t('v2.shell.user-menu.profile') }}
          </button>
        </MenuItem>
        <MenuItem>
          <button type="button" :class="menuItemClass" @click="$emit('changePassword')">
            <KeyRound class="h-4 w-4 text-app-muted" aria-hidden="true" />
            {{ t('v2.shell.user-menu.change-password') }}
          </button>
        </MenuItem>
        <MenuItem>
          <button type="button" :class="menuItemClass" @click="onSwitchLanguage">
            <Languages class="h-4 w-4 text-app-muted" aria-hidden="true" />
            {{ t('v2.shell.user-menu.switch-language') }}
            <span class="ml-auto text-xs text-app-muted">{{
              locale === 'en' ? 'EN' : 'ខ្មែរ'
            }}</span>
          </button>
        </MenuItem>
        <div class="my-1 h-px bg-app-border-soft" />
        <MenuItem>
          <button type="button" :class="[menuItemClass, 'text-red-700']" @click="$emit('signOut')">
            <LogOut class="h-4 w-4" aria-hidden="true" />
            {{ t('v2.shell.user-menu.sign-out') }}
          </button>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>
